import React, { useEffect, useState } from 'react';
import { Popover, Row, Col, ListGroup, Spinner, PopoverProps } from 'react-bootstrap';

import { parseISO, format } from 'date-fns';

import {
  BrregEnhetResponse,
  BrregEnhetResponseAddress,
} from 'services/KjederegisteretAdminApi/BrregApi/types';
import BrregApi from 'services/KjederegisteretAdminApi/BrregApi';

import Style from './index.module.css';

type BrregPopoverProps = PopoverProps & {
  data: BrregEnhetResponse;
  dagligLeder?: string;
};

const BrregPopover = React.forwardRef<HTMLDivElement, BrregPopoverProps>(
  ({ data, dagligLeder, className, ...props }, ref) => {
    const [underEnhetCount, setUnderEnhetCount] = useState<number | null>(null);
    const [underEnhetLoading, setUnderEnhetLoading] = useState(false);
    const [underEnhetError, setUnderEnhetError] = useState<string | null>(null);
    const [oppstartsdato, setOppstartsdato] = useState<Date | null>(null);

    useEffect(() => {
      let isCancelled = false;

      const fetchUnderEnhetCount = async () => {
        const orgNr = data?.orgNr?.trim();
        if (!orgNr) {
          setUnderEnhetCount(null);
          return;
        }

        setUnderEnhetLoading(true);
        setUnderEnhetError(null);

        try {
          const count = await BrregApi.getUnderEnhetCount(orgNr);
          if (count === 1 && !oppstartsdato) {
            const result = await BrregApi.getOppstartsdato(orgNr);
            setOppstartsdato(result);
          }
          if (!isCancelled) {
            setUnderEnhetCount(count);
          }
        } catch (err) {
          if (!isCancelled) {
            setUnderEnhetCount(null);
            setUnderEnhetError('Kunne ikke hente antall underenheter');
            // eslint-disable-next-line no-console
            console.error('BrregPopover: Feil ved henting av underenhet-tall', err);
          }
        } finally {
          if (!isCancelled) {
            setUnderEnhetLoading(false);
          }
        }
      };

      fetchUnderEnhetCount();

      return () => {
        isCancelled = true;
      };
    }, [data?.orgNr]);

    useEffect(() => {
      setOppstartsdato(data?.oppstartsdato);
    }, [data?.oppstartsdato]);

    const brregDetailUrl = data?.orgNr
      ? `https://virksomhet.brreg.no/nb/oppslag/enheter/${encodeURIComponent(data.orgNr.trim())}`
      : null;

    const formatAddress = (adress: BrregEnhetResponseAddress) => {
      if (adress.co) {
        return (
          <div>
            {adress.co}
            <br />
            {adress.address}
            <br />
            {adress.postalCode} {adress.postalPlace} <br /> {adress.countryCode}{' '}
          </div>
        );
      } else {
        return (
          <div>
            {adress.address}
            <br />
            {adress.postalCode} {adress.postalPlace} <br /> {adress.countryCode}{' '}
          </div>
        );
      }
    };

    const formatDate = (val: string) => {
      const parsed = parseISO(val);
      return format(parsed, 'dd.MM.yyyy');
    };

    return (
      <Popover
        ref={ref}
        className={`${Style.popover} ${className ?? ''}`}
        id="popover-menu"
        {...props}
      >
        <Popover.Header>Juridisk informasjon</Popover.Header>
        <Popover.Body className={Style.popoverContent}>
          {data && (
            <Row>
              <Col className={Style.popoverContentCol}>
                <ListGroup variant="flush">
                  <ListGroup.Item>
                    <strong>Navn: </strong>
                    {brregDetailUrl ? (
                      <a href={brregDetailUrl} target="_blank" rel="noopener noreferrer">
                        {data.name}
                      </a>
                    ) : (
                      data.name
                    )}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Konkurs: </strong>
                    {data.konkurs ? 'JA' : 'NEI'}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Under avvikling: </strong>
                    {data.underAvvikling ? 'JA' : 'NEI'}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Under tvangsavvikling : </strong>
                    {data.underTvangsavviklingEllerTvangsopplosning ? 'JA' : 'NEI'}
                  </ListGroup.Item>

                  <ListGroup.Item>
                    <strong>Registrert dato: </strong>
                    <span>{data.regDato ? formatDate(data.regDato.toString()) : ''}</span>
                  </ListGroup.Item>
                  {data.slettedato && (
                    <ListGroup.Item variant="warning">
                      <strong>Slettet dato: </strong>
                      <span>{formatDate(data.slettedato.toISOString()) || ''}</span>
                    </ListGroup.Item>
                  )}
                  {oppstartsdato && (
                    <ListGroup.Item>
                      <div>
                        <span>
                          <strong>Oppstartsdato: </strong>
                        </span>
                        <span>{oppstartsdato ? format(oppstartsdato, 'dd.MM.yyyy') : ''}</span>
                      </div>
                    </ListGroup.Item>
                  )}
                  <ListGroup.Item>
                    <div>
                      <span>
                        <strong>Næringskode: </strong>
                      </span>
                      <span>{data.naeringskode1?.kode}</span>
                    </div>

                    <div>
                      <span>
                        <strong>Beskrivelse: </strong>
                      </span>
                      <span>{data.naeringskode1?.beskrivelse}</span>
                    </div>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <div>
                      <span>
                        <strong>Næringskode 2: </strong>
                      </span>
                      <span>{data.naeringskode2?.kode}</span>
                    </div>

                    <div>
                      <span>
                        <strong>Beskrivelse 2: </strong>
                      </span>
                      <span>{data.naeringskode2?.beskrivelse}</span>
                    </div>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Organisasjonsform: </strong>
                    {data.organisasjonsform}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Antall underenheter: </strong>
                    {underEnhetLoading ? (
                      <Spinner animation="border" size="sm" />
                    ) : underEnhetError ? (
                      <span className="text-warning">{underEnhetError}</span>
                    ) : underEnhetCount !== null ? (
                      underEnhetCount
                    ) : (
                      'Ukjent'
                    )}
                  </ListGroup.Item>
                </ListGroup>
              </Col>
              <Col className={Style.popoverContentCol}>
                <ListGroup variant="flush">
                  <ListGroup.Item>
                    <strong>Antall ansatte: </strong>
                    {data.antallAnsatte}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Forretningsadresse: </strong>
                    {data && data.forretningsAddress && formatAddress(data.forretningsAddress)}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Postadresse: </strong>
                    {data && data.postAddress && formatAddress(data.postAddress)}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Hjemmeside: </strong>
                    {data && data.hjemmeside && (
                      <a
                        href={
                          data.hjemmeside.startsWith('http://') ||
                          data.hjemmeside.startsWith('https://')
                            ? data.hjemmeside
                            : `https://${data.hjemmeside}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {data.hjemmeside}
                      </a>
                    )}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Epost: </strong>
                    {data && data.epostadresse && (
                      <a
                        href={`mailto:${data.epostadresse}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {data.epostadresse}
                      </a>
                    )}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Telefon: </strong>
                    {data && data.telefon && (
                      <a href={`tel:${data.telefon}`} target="_blank" rel="noopener noreferrer">
                        {data.telefon}
                      </a>
                    )}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Daglig leder: </strong>
                    {dagligLeder ? dagligLeder : 'Ingen registrert'}
                  </ListGroup.Item>
                  {data.orgNr && (
                    <ListGroup.Item>
                      <a
                        href={`https://w2.brreg.no/kunngjoring/hent_nr.jsp?orgnr=${data.orgNr.trim()}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Kunngjøringer
                      </a>
                    </ListGroup.Item>
                  )}
                </ListGroup>
                {data.finnesIkke && (
                  <ListGroup.Item variant="warning">
                    <strong>Finnes ikke: </strong>
                    {data.finnesIkke && 'JA'}
                  </ListGroup.Item>
                )}
              </Col>
            </Row>
          )}
        </Popover.Body>
      </Popover>
    );
  },
);

BrregPopover.displayName = 'BrregPopover';

export default BrregPopover;
